import * as React from 'react';
import { type DateValue, RangeCalendarContext, useSlottedContext } from 'react-aria-components';

import { cx } from '../../utils/cx';

import { type CalendarDateRange } from './types';

interface IRangePresetButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  value: CalendarDateRange;
}

/**
 * Renders a preset button within a RangeCalendar that highlights itself when its
 * `value` matches the current calendar selection. Reads from `RangeCalendarContext`
 * to determine the active state.
 */
export const RangePresetButton = ({ value, className, children, ...props }: IRangePresetButtonProps) => {
  const context = useSlottedContext(RangeCalendarContext);

  // Since context uses DateValue internally, we need to cast for comparison
  const dateValue = value as { start: DateValue; end: DateValue };

  const isSelected =
    context?.value?.start?.compare(dateValue.start) === 0 && context?.value?.end?.compare(dateValue.end) === 0;

  return (
    <button
      {...props}
      className={cx(
        'outline-focus-ring cursor-pointer rounded-md px-3 py-2 text-left text-sm font-medium transition duration-100 ease-linear focus-visible:outline-2 focus-visible:outline-offset-2',
        isSelected
          ? 'bg-active text-secondary_hover hover:bg-secondary_hover'
          : 'text-secondary hover:bg-primary_hover hover:text-secondary_hover',
        className
      )}
    >
      {children}
    </button>
  );
};
